/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Assign unique coupons to audience members in a journey", description = "Use a warehouse table of ready-to-use coupon codes to assign one unique code to each audience member, then sync the code to your destinations.", lastUpdatedDate = new Date(1789603200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Assign unique coupons to audience members in a journey",
        description: "Use a warehouse table of ready-to-use coupon codes to assign one unique code to each audience member, then sync the code to your destinations.",
        lastUpdatedDate: "Thu Sep 17 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What coupon assignment does",
          "depth": 0,
          "id": "what-coupon-assignment-does"
        }, {
          "value": "When to use coupon assignment",
          "depth": 0,
          "id": "when-to-use-coupon-assignment"
        }, {
          "value": "1. Prepare the coupon table",
          "depth": 0,
          "id": "1-prepare-the-coupon-table"
        }, {
          "value": "2. Validate the coupon data",
          "depth": 0,
          "id": "2-validate-the-coupon-data"
        }, {
          "value": "Check for duplicate coupon codes",
          "depth": 1,
          "id": "check-for-duplicate-coupon-codes"
        }, {
          "value": "Check the available inventory",
          "depth": 1,
          "id": "check-the-available-inventory"
        }, {
          "value": "3. Add the coupon model to your schema",
          "depth": 0,
          "id": "3-add-the-coupon-model-to-your-schema"
        }, {
          "value": "4. Set up the audience and journey",
          "depth": 0,
          "id": "4-set-up-the-audience-and-journey"
        }, {
          "value": "5. Add an Assign coupon step",
          "depth": 0,
          "id": "5-add-an-assign-coupon-step"
        }, {
          "value": "6. Sync the assigned values",
          "depth": 0,
          "id": "6-sync-the-assigned-values"
        }, {
          "value": "7. Test before launch",
          "depth": 0,
          "id": "7-test-before-launch"
        }, {
          "value": "Behavior and constraints",
          "depth": 0,
          "id": "behavior-and-constraints"
        }, {
          "value": "Track assignments",
          "depth": 0,
          "id": "track-assignments"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      code: "code",
      ul: "ul",
      li: "li",
      pre: "pre",
      span: "span",
      h3: "h3",
      ol: "ol",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A ", _jsx(_components.a, {
                    href: "/customer-studio/journeys",
                    children: "journey"
                  }), " with an audience-based entry"]
                }), _jsxs("li", {
                  children: ["A warehouse table of coupon codes in your ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "schema"
                  })]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Coupon assignment gives each audience member a unique value — such as a single-use coupon code — inside a journey, then makes it available to sync to your destinations."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "what-coupon-assignment-does",
        children: "What coupon assignment does"
      }), "\n", _jsx(_components.p, {
        children: "Coupon assignment matches one available coupon row to each member of an audience inside a journey. The assigned coupon becomes a journey variable that you can use in messages or syncs."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if your audience has 1,000 members and your coupon table has 1,000 available codes, Hightouch assigns one code to each member. Each member receives a different code, such as ", _jsx(_components.code, {
          children: "SUMMER10-X7Q2"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch assigns codes that already exist in your warehouse. It doesn't generate coupon codes, and it doesn't assign coupons to people outside the audience that enters the journey."
      }), "\n", _jsx(_components.h2, {
        id: "when-to-use-coupon-assignment",
        children: "When to use coupon assignment"
      }), "\n", _jsx(_components.p, {
        children: "Use coupon assignment when all of the following are true:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Each audience member needs a unique value, such as a single-use coupon code."
        }), "\n", _jsx(_components.li, {
          children: "The values come from a shared dataset, such as a table of codes from your coupon or commerce system."
        }), "\n", _jsx(_components.li, {
          children: "You want to assign values only to members of a specific campaign or journey."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If every member should receive the same value, such as ", _jsx(_components.code, {
          children: "SAVE20"
        }), ", use a ", _jsx(_components.a, {
          href: "/customer-studio/journeys/tiles#set-variable",
          children: "Set variable"
        }), " tile instead. Coupon assignment is for one-per-member values."]
      }), "\n", _jsx(_components.h2, {
        id: "1-prepare-the-coupon-table",
        children: "1. Prepare the coupon table"
      }), "\n", _jsx(_components.p, {
        children: "Create a table in your warehouse that contains the coupons you want Hightouch to assign. Your coupon or commerce system must create the codes first; Hightouch only assigns existing values."
      }), "\n", _jsx(_components.p, {
        children: "A coupon table usually includes these columns:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Example"
            }), _jsx(_components.th, {
              children: "What it is used for"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "coupon_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "SUMMER26-000001"
              })
            }), _jsx(_components.td, {
              children: "Unique row identifier. Use this as the model's primary key."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "coupon_code"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "SUMMER10-X7Q2"
              })
            }), _jsx(_components.td, {
              children: "The unique value assigned to an audience member."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "campaign_id"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "summer_winback_2026"
              })
            }), _jsx(_components.td, {
              children: "Separates one coupon pool from another campaign's pool."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "status"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "available"
              })
            }), _jsx(_components.td, {
              children: "Identifies codes that can still be assigned."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "expiration_date"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "2026-09-30"
              })
            }), _jsx(_components.td, {
              children: "Identifies codes that should not be assigned after a date."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "discount_amount"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "10.00"
              })
            }), _jsx(_components.td, {
              children: "Optional information to pass to a destination."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Add any other metadata your messages need, such as ", _jsx(_components.code, {
          children: "minimum_purchase_amount"
        }), " or ", _jsx(_components.code, {
          children: "currency"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Build a model that normalizes these fields and returns only available, non-expired codes. Keep fields such as ", _jsx(_components.code, {
          children: "campaign_id"
        }), " and ", _jsx(_components.code, {
          children: "region"
        }), " in the model if you want to filter the coupon pool in the journey."]
      }), "\n", _jsx(_components.p, {
        children: "This Snowflake example creates that model:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "NULLIF"
          }), "(", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "TRIM"
          }), "(TO_VARCHAR(coupon_id)), ", _jsx(_components.span, {
            className: "hljs-string",
            children: "''"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " coupon_id,\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "NULLIF"
          }), "(", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "TRIM"
          }), "(TO_VARCHAR(coupon_code)), ", _jsx(_components.span, {
            className: "hljs-string",
            children: "''"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " coupon_code,\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "NULLIF"
          }), "(", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "TRIM"
          }), "(TO_VARCHAR(campaign_id)), ", _jsx(_components.span, {
            className: "hljs-string",
            children: "''"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " campaign_id,\n  TRY_TO_DATE(TO_VARCHAR(expiration_date)) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " expiration_date,\n  TRY_TO_DECIMAL(TO_VARCHAR(discount_amount), ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), ", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " discount_amount,\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "UPPER"
          }), "(", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "NULLIF"
          }), "(", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "TRIM"
          }), "(TO_VARCHAR(region)), ", _jsx(_components.span, {
            className: "hljs-string",
            children: "''"
          }), ")) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " region\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " RAW.MARKETING.COUPON_POOL\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "NULLIF"
          }), "(", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "TRIM"
          }), "(TO_VARCHAR(coupon_code)), ", _jsx(_components.span, {
            className: "hljs-string",
            children: "''"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "LOWER"
          }), "(status) ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'available'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " TRY_TO_DATE(TO_VARCHAR(expiration_date)) ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CURRENT_DATE"
          }), "();\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "2-validate-the-coupon-data",
        children: "2. Validate the coupon data"
      }), "\n", _jsx(_components.p, {
        children: "Before you add the model to a journey, check that the assignment column contains unique values and that the pool is large enough for your audience."
      }), "\n", _jsx(_components.h3, {
        id: "check-for-duplicate-coupon-codes",
        children: "Check for duplicate coupon codes"
      }), "\n", _jsxs(_components.p, {
        children: ["The assignment column must contain unique values. If two rows contain the same code, correct the source data. Don't hide duplicates with ", _jsx(_components.code, {
          children: "ROW_NUMBER()"
        }), " because that can cause the same code to be assigned more than once."]
      }), "\n", _jsx(_components.p, {
        children: "This query should return zero rows:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " coupon_code, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COUNT"
          }), "(", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " code_count\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " RAW.MARKETING.COUPON_POOL\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " coupon_code ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GROUP"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " coupon_code\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "HAVING"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COUNT"
          }), "(", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ") ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ";\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "check-the-available-inventory",
        children: "Check the available inventory"
      }), "\n", _jsx(_components.p, {
        children: "Make sure each coupon pool contains at least as many available codes as the number of audience members who will use it. If the pool is smaller than the audience, Hightouch fails the journey instead of assigning coupons to only part of the audience."
      }), "\n", _jsx(_components.p, {
        children: "This query shows the number of available codes in each campaign pool:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " campaign_id, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COUNT"
          }), "(", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " available_codes\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " RAW.MARKETING.COUPON_POOL\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "LOWER"
          }), "(status) ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'available'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " expiration_date ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CURRENT_DATE"
          }), "()\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GROUP"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " campaign_id;\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Compare the result with the size of the audience that will use each pool. If you filter by more than ", _jsx(_components.code, {
          children: "campaign_id"
        }), ", include those filters when you calculate the inventory."]
      }), "\n", _jsx(_components.h2, {
        id: "3-add-the-coupon-model-to-your-schema",
        children: "3. Add the coupon model to your schema"
      }), "\n", _jsxs(_components.p, {
        children: ["Add the coupon table or SQL model to your Customer Studio ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "schema"
        }), ". A coupon model is the source of the values Hightouch can assign; it does not contain the member-to-coupon assignments yet."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Customer Studio > Schema"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Create a parent model from your coupon table or SQL model."
        }), "\n", _jsxs(_components.li, {
          children: ["Set the model's ", _jsx(_components.strong, {
            children: "data type"
          }), " to ", _jsx(_components.strong, {
            children: "Coupons"
          }), ". This makes the model available in the ", _jsx(_components.strong, {
            children: "Assign coupon"
          }), " step."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set the primary key to a unique row identifier, such as ", _jsx(_components.code, {
            children: "coupon_id"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Save the model and confirm that the preview shows the expected codes and columns."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The coupon model must use the same source as the journey's parent model. It contains the pool of unassigned values. Hightouch creates the member-to-coupon association when a member reaches the ", _jsx(_components.strong, {
          children: "Assign coupon"
        }), " step."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/coupons/cs-coupon-table.png",
          alt: "Coupon model with the Coupons data type in Customer Studio Schema"
        })
      }), "\n", _jsx(_components.h2, {
        id: "4-set-up-the-audience-and-journey",
        children: "4. Set up the audience and journey"
      }), "\n", _jsxs(_components.p, {
        children: ["Build the audience for your campaign, then create a ", _jsx(_components.a, {
          href: "/customer-studio/journeys",
          children: "journey"
        }), " that uses this audience as its entry. Only members who enter through this audience receive a coupon."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/coupons/cs-coupon-entry-audience.png",
          alt: "Audience-based journey entry for a coupon campaign"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Coupon assignment runs only in audience-based journeys. The ", _jsx(_components.strong, {
            children: "Assign coupon"
          }), "\ntile can appear on an event-triggered journey's canvas, but you can't save or\nactivate an event-triggered journey that contains coupon assignment. To build\nan event-driven campaign, first build an audience from the event, then use\nthat audience as the journey entry."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "5-add-an-assign-coupon-step",
        children: "5. Add an Assign coupon step"
      }), "\n", _jsxs(_components.p, {
        children: ["Add an ", _jsx(_components.strong, {
          children: "Assign coupon"
        }), " step to the canvas after the entry step. The step selects one row from the coupon pool for each member who reaches it."]
      }), "\n", _jsx(_components.p, {
        children: "Configure the step as follows:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select your coupon model as the ", _jsx(_components.strong, {
            children: "Coupon source"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) Add a ", _jsx(_components.strong, {
            children: "Filter coupon pool"
          }), " condition to narrow the rows that can be assigned. For example, filter ", _jsx(_components.code, {
            children: "campaign_id"
          }), " to ", _jsx(_components.code, {
            children: "summer_winback_2026"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose the ", _jsx(_components.strong, {
            children: "Assignment column"
          }), ". This is the unique value each member receives, such as ", _jsx(_components.code, {
            children: "coupon_code"
          }), ". The column must contain unique text or number values."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set a ", _jsx(_components.strong, {
            children: "Display name"
          }), ". Hightouch uses this name for the journey variable and the label that appears in downstream syncs."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) Under ", _jsx(_components.strong, {
            children: "Additional columns"
          }), ", add metadata such as ", _jsx(_components.code, {
            children: "expiration_date"
          }), " or ", _jsx(_components.code, {
            children: "discount_amount"
          }), ". Give each column its own display name. These columns don't need to be unique."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Preview results"
          }), " to check the matching rows and confirm that the pool has enough codes."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Update"
          }), " to save the step."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/coupons/cs-coupon-node.png",
          alt: "Assign coupon step configured with a coupon source, pool filter, and assignment column"
        })
      }), "\n", _jsx(_components.p, {
        children: "When a member reaches this step, Hightouch assigns a code and stores the code and any additional columns as journey variables. You can use these variables in later journey steps and in journey logs."
      }), "\n", _jsx(_components.h2, {
        id: "6-sync-the-assigned-values",
        children: "6. Sync the assigned values"
      }), "\n", _jsxs(_components.p, {
        children: ["Add a ", _jsx(_components.strong, {
          children: "Send to destination"
        }), " step to pass the assigned variables to a destination such as Braze or Salesforce Marketing Cloud. Map each journey variable to the destination field where you want to store or use it."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Journey variable"
            }), _jsx(_components.th, {
              children: "Example destination field"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "coupon_code"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "promo_code"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "expiration_date"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "promo_expires_at"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "discount_amount"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "promo_amount"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If the variables don't appear in the step, save the journey, then reopen it."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/coupons/cs-coupon-syncs.png",
          alt: "Assigned coupon variables mapped to destination fields"
        })
      }), "\n", _jsx(_components.h2, {
        id: "7-test-before-launch",
        children: "7. Test before launch"
      }), "\n", _jsx(_components.p, {
        children: "Test with a small audience and confirm the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Each member receives a different ", _jsx(_components.code, {
            children: "coupon_code"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Every assigned code is available and has not expired."
        }), "\n", _jsx(_components.li, {
          children: "The metadata matches the assigned coupon row."
        }), "\n", _jsx(_components.li, {
          children: "The destination receives the expected fields."
        }), "\n", _jsxs(_components.li, {
          children: ["Re-entering the same ", _jsx(_components.strong, {
            children: "Assign coupon"
          }), " step reuses the original code."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "behavior-and-constraints",
        children: "Behavior and constraints"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Inventory:"
          }), " If a coupon pool has fewer available codes than the audience that uses it, the journey fails before sending. Keep more codes than members, and configure ", _jsx(_components.a, {
            href: "/customer-studio/journeys/monitor#configure-alerts",
            children: "alerting"
          }), " for always-on journeys so you're notified when the pool is running low."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Consistent assignment:"
          }), " A member keeps the same coupon and metadata when they re-enter the same step, even if the coupon table changes. To assign a different value, add a separate ", _jsx(_components.strong, {
            children: "Assign coupon"
          }), " step with a non-overlapping pool."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Expiration:"
          }), " Filter out expired and unavailable codes in the model or the pool filter so new members don't receive them. Hightouch preserves an existing assignment even if that code later expires."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Separate pools across steps and journeys:"
          }), " Hightouch assigns a unique value to each member within one ", _jsx(_components.strong, {
            children: "Assign coupon"
          }), " step. It doesn't deduplicate assignments across different steps or journeys. If two steps draw from overlapping rows in the same coupon model, the same code can be assigned twice. Partition the pool by ", _jsx(_components.code, {
            children: "campaign_id"
          }), ", or use separate tables, so each step draws from non-overlapping rows."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "track-assignments",
        children: "Track assignments"
      }), "\n", _jsxs(_components.p, {
        children: ["Assignments are recorded in journey logs. Use the logs to map coupons to members or join assignments with redemption data for reporting. See ", _jsx(_components.a, {
          href: "/developer-tools/warehouse-tables#journey-tables",
          children: "Journey tables"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys/tiles#assign-an-offer",
            children: "Journey tiles"
          }), " to configure the ", _jsx(_components.strong, {
            children: "Assign coupon"
          }), " tile alongside other tiles."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys/create",
            children: "Create a journey"
          }), " to build the audience-based journey that assigns coupons."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys/tiles#send-to-destination",
            children: "Send to destination tiles"
          }), " to send assigned values to a destination."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
